import React from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import Helmet from 'react-helmet';
import { graphql, Link } from 'gatsby';
import { BlogBreadCrumbs } from '../components/blog-bread-crumbs';
import Layout from '../components/Layout';
import Content, { HTMLContent } from '../components/Content';
import { PostList } from '../components/post-list';
import PreviewCompatibleImage from '../components/PreviewCompatibleImage';
import Share from '../components/Share';
import { SignUpComponent } from '../components/sign-up/sign-up-component';
import { PostTools } from '../lib/post-tools';

export const BlogPostTemplate = ({
  content,
  contentComponent,
  description,
  tags,
  title,
  helmet,
  slug,
  url,
  twitterHandle,
  image,
  mostRecentPosts,
}) => {
  const PostContent = contentComponent || Content;

  return (
    <section className="section">
      {helmet || ''}
      <div className="container content">
        <div className="columns">
          <div className="column is-10 is-offset-1">
            <h1 className="title is-size-2 has-text-weight-bold is-bold-light">
              {title}
            </h1>
            <BlogBreadCrumbs tags={tags} title={title} />
            <div className="columns">
              <div className="column is-8">
                <PreviewCompatibleImage
                  imageInfo={{
                    image: image,
                    alt: `featured image thumbnail for post ${title}`,
                  }}
                />
                <p>{description}</p>
                <PostContent content={content} />
                {tags && tags.length ? (
                  <div style={{ marginTop: `4rem` }}>
                    <h4>Tags</h4>
                    <ul className="taglist">
                      {tags.map(tag => (
                        <li key={tag + `tag`}>
                          <Link to={`/tags/${kebabCase(tag)}/`}>{tag}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <div className="column is-4">
                <SignUpComponent />
                <hr style={{marginTop: '18px', marginBottom: '18px'}}/>
                <PostList posts={mostRecentPosts} />
              </div>
            </div>
            <Share
              socialConfig={{
                twitterHandle: twitterHandle || '',
                config: {
                  url: `${url}${slug}`,
                  title,
                },
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

BlogPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  contentComponent: PropTypes.func,
  description: PropTypes.string,
  title: PropTypes.string,
  helmet: PropTypes.object,
};

const BlogPost = ({ data }) => {
  const post = data.post;
  const site = data.site;
  const allPosts = data.allPosts;
  return (
    <Layout>
      <BlogPostTemplate
        content={post.html}
        contentComponent={HTMLContent}
        description={post.frontmatter.description}
        helmet={
          <Helmet titleTemplate="%s | Blog">
            <title>{`${post.frontmatter.title}`}</title>
            <meta
              name="description"
              content={`${post.frontmatter.description}`}
            />
          </Helmet>
        }
        tags={post.frontmatter.tags}
        title={post.frontmatter.title}
        image={post.frontmatter.featuredimage}
        mostRecentPosts={PostTools.mostRecent(PostTools.edgesToArray(allPosts))}
        url={site.siteMetadata.url}
        slug={post.fields.slug}
      />
    </Layout>
  );
};

BlogPost.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
  }),
};

export default BlogPost;

export const pageQuery = graphql`
  query BlogPostByID($id: String!) {
    site {
      siteMetadata {
        url
        twitterHandle
      }
    }
    post: markdownRemark(id: { eq: $id }) {
      id
      html
      fields {
        slug
      }
      frontmatter {
        date(formatString: "MMMM DD, YYYY")
        title
        description
        tags
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 4096, quality: 100) {
              ...GatsbyImageSharpFluid
            }
          }
        }
      }
    }
    allPosts: allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: { frontmatter: { templateKey: { eq: "blog-post" } } }
    ) {
      ...PostsQuery
    }
  }
`;
